import type { CustomQuestion } from "../../types";

type SaveStatus = "idle" | "saving" | "saved";

interface CustomQuestionInputsProps {
  questions: CustomQuestion[];
  /** Current answers keyed by question id, as input strings. */
  values: Record<string, string>;
  onChange: (question: CustomQuestion, value: string) => void;
  /** Per-question autosave state, keyed by question id. */
  status?: Record<string, SaveStatus>;
}

/**
 * The place's questions answered on the activity — "Song" on Guardians, "Booth
 * name" at the festival stand. One input per question, each saving on its own,
 * so the second answer doesn't wait for the first to land.
 */
export function CustomQuestionInputs({ questions, values, onChange, status = {} }: CustomQuestionInputsProps) {
  if (questions.length === 0) return null;
  return (
    <div className="custom-questions">
      {questions.map((q) => {
        const value = values[q.id] ?? "";
        const st = status[q.id] ?? "idle";
        const missing = q.required && value.trim() === "";
        const inputId = `custom-question-${q.id}`;
        const common = {
          id: inputId,
          className: "form-input",
          value,
          onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
            onChange(q, e.target.value),
          "aria-required": q.required || undefined,
        };
        return (
          <div key={q.id} className={`custom-question${missing ? " custom-question-missing" : ""}`}>
            <div className="live-notes-header">
              <label className="live-notes-label" htmlFor={inputId}>
                {q.label}
                {q.required && (
                  <span className="custom-question-req" title="Required"> *</span>
                )}
              </label>
              {st === "saving" && <span className="live-notes-status">Saving…</span>}
              {st === "saved" && <span className="live-notes-status live-notes-status-saved">Saved</span>}
            </div>
            {q.type === "select" ? (
              <select {...common}>
                <option value="">—</option>
                {(q.options ?? []).map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
                {/* An answer carried over from before the choices changed still shows. */}
                {value && !(q.options ?? []).includes(value) && (
                  <option value={value}>{value}</option>
                )}
              </select>
            ) : q.type === "textarea" ? (
              <textarea {...common} rows={3} />
            ) : q.type === "number" ? (
              <input {...common} type="number" inputMode="decimal" />
            ) : q.type === "date" ? (
              <input {...common} type="date" />
            ) : q.type === "time" ? (
              <input {...common} type="time" />
            ) : q.type === "datetime" ? (
              <input {...common} type="datetime-local" />
            ) : (
              <input {...common} type="text" />
            )}
          </div>
        );
      })}
    </div>
  );
}
